// The SUBPATH, not the barrel: the package index pulls in zod and the whole
// profanity word list, and this page is the top of the install funnel — every
// kilobyte here is paid for by somebody on a phone deciding whether to bother.
// `shareLinks` has no imports of its own.
import { shareLinkSource } from "@bible-shield/shared/shareLinks";
import { useEffect } from "react";

import { type Store, useTrack } from "../lib/analytics";
// Where someone without the app is sent. One module, because this page and the
// landing page both sell the same app — see lib/stores.ts.
import { appStoreLink } from "../lib/stores";
import { PageChrome } from "./PageChrome";

/**
 * What a shared link shows to someone who does NOT have the app.
 *
 * DELIBERATELY DOES NOT RENDER THE SHARED CONTENT. This was the open question
 * in the plan and it is answered here rather than left to whoever wires the
 * loader next.
 *
 * Everything shareable lives behind the community terms gate: you accept them
 * before the wall renders, and the wall carries prayer requests — the most
 * intimate thing anybody writes in this app, frequently posted anonymously
 * BECAUSE it is intimate. Members wrote it for members. Putting the body on an
 * open web page would republish it to anyone with the URL and to every crawler
 * that follows it, which is a change to the privacy model that no member agreed
 * to and that cannot be taken back once indexed.
 *
 * So the page confirms that something was shared, says what kind of thing it is,
 * and offers the app. Somebody who has the app never sees this at all — iOS and
 * Android hand the URL straight to it. The person who does see it is exactly the
 * person who has not agreed to the community's terms.
 *
 * Loosening this later is a product decision with a mechanism attached (a public
 * flag on a post, say, opted into at publish time) — not a matter of adding a
 * fetch here. `noindex` is on for the same reason: even the shape of the URL
 * space should not become a crawlable directory of members' requests.
 */
/**
 * Which share surface sent this visitor — `utm_source`, written into the link by
 * `shareLinkUrl` and read back with the same rule the app's deep-link parser
 * uses, so a value that reaches PostHog is one we could have written (GEN-12).
 *
 * Read from `window.location` rather than the router because both callers are
 * client-only (an effect and a click handler) and analytics never runs on the
 * Worker — and because posthog-js does NOT put campaign parameters on the event
 * itself: with `person_profiles: "never"` they are neither set on a person nor
 * captured as properties, leaving `utm_source` visible only inside the
 * `$current_url` string, which no funnel can be broken down by.
 */
function shareSource(): string | null {
  if (typeof window === "undefined") return null;
  return shareLinkSource(window.location.search);
}

export function SharedItem({ kind }: { kind: "post" | "request" }) {
  const track = useTrack();

  // Reaching this page at all is the measurable fact: an installed app
  // intercepts the URL before a browser ever sees it, so every one of these is
  // somebody who does not have Bible Shield yet. `kind` and the surface that
  // produced the link — never the share code in the path, which identifies one
  // member's request and which this page otherwise refuses to resolve.
  useEffect(() => {
    track("web_share_landing_viewed", { kind, source: shareSource() });
  }, [track, kind]);

  // The surface rides onto the store click too. This is the one event on the
  // page that is an install intent rather than a visit, so without it "which
  // share surface actually brings users" stops one step short of the answer.
  const openStore = (store: Store) => {
    track("web_store_click", {
      store,
      from: `share_${kind}`,
      source: shareSource(),
    });
  };

  const title =
    kind === "request"
      ? "Someone shared a prayer request with you"
      : "Someone shared a post with you";
  const blurb =
    kind === "request"
      ? "Prayer requests live inside the Bible Shield community, where the people who read them have agreed to its terms. Open the app to read this one and pray for it."
      : "Posts live inside the Bible Shield community. Open the app to read this one.";

  return (
    <PageChrome title={title}>
      <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-ink2">
        {blurb}
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <a
          href={appStoreLink(`share_${kind}`)}
          onClick={() => openStore("appstore")}
          className="rim inline-flex h-[54px] items-center justify-center bg-brand px-7 font-display text-[17px] font-bold text-onbrand"
        >
          get it on the app store
        </a>
        {/* A status, not a button: Bible Shield is not on Google Play yet.
            Someone who opened a shared post on an Android phone still needs to
            be told that, and a dead store link tells them the wrong thing. */}
        <span
          aria-disabled="true"
          className="inline-flex h-[54px] items-center justify-center rounded-pill border-[2.5px] border-dashed border-ink2 bg-inset px-7 font-display text-[17px] font-bold text-body"
        >
          android — coming soon
        </span>
      </div>

      <p className="mt-7 max-w-2xl text-[15px] leading-relaxed text-ink2">
        Already have Bible Shield? Open this link again from your phone and it
        will take you straight there.
      </p>

      <div className="brutal mt-12 max-w-2xl px-5 py-4 text-[15px] leading-relaxed text-ink2">
        We don’t show community writing on the open web. What people share in
        Bible Shield stays inside it.
      </div>
    </PageChrome>
  );
}

/** Head tags every shared-link page needs: a title, and no indexing. */
export function sharedItemHead(kind: "post" | "request") {
  const title =
    kind === "request"
      ? "Shared prayer request — Bible Shield"
      : "Shared post — Bible Shield";
  return {
    meta: [
      { title },
      // Not a directory of members' prayer requests, however thin each page is.
      { name: "robots", content: "noindex, nofollow" },
    ],
  };
}
